import CustomModal from "components/lib/customModal";
import { useCallback, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { Trans, useTranslation } from "react-i18next";
import { Container, CropContainer } from "./cropModal.styles";
import { getCroppedImg } from "./croputils";

type CropProps = {
  open: boolean;
  photoURL: string;
  onSave: (cropped: string) => void;
  onClose: () => void;
  onUploadNew: () => void;
};

const CropModal = ({
  open,
  photoURL,
  onSave,
  onClose,
  onUploadNew,
}: CropProps) => {
  const { t } = useTranslation();

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixel: Area) => {
      setCroppedAreaPixels(croppedAreaPixel);
    },
    [],
  );

  const handleSave = async () => {
    const cropped = await getCroppedImg(photoURL, croppedAreaPixels, rotation);
    if (cropped) {
      onSave(cropped);
    }
  };

  return (
    <CustomModal.Wrapper
      size="large"
      onClose={() => onClose()}
      titleText={t("CROP_IMAGE_MODAL_TITLE")}
      open={open}
    >
      <CustomModal.Column>
        <Container>
          <CropContainer>
            <Cropper
              image={photoURL}
              crop={crop}
              zoom={zoom}
              aspect={25 / 23}
              rotation={rotation}
              zoomSpeed={4}
              maxZoom={3}
              zoomWithScroll={true}
              showGrid={true}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
            />
          </CropContainer>
        </Container>
      </CustomModal.Column>
      <CustomModal.Paragraph>
        <Trans
          i18nKey="CROP_IMAGE_MODAL_DESCRIPTION"
          components={{
            strong: <strong />,
            em: <em />,
            li: <li />,
            ul: <ul />,
            h4: <h4 />,
            a: <a />,
          }}
        />
      </CustomModal.Paragraph>

      <CustomModal.ButtonRow paddingTop={24}>
        <CustomModal.Button
          variant="secondary"
          onClick={() => onUploadNew()}
          text={t("UPLOAD_NEW")}
        />
        <CustomModal.Button
          variant="primary"
          onClick={() => handleSave()}
          text={t("SAVE")}
        />
      </CustomModal.ButtonRow>
    </CustomModal.Wrapper>
  );
};

export default CropModal;
